import type { NavItem } from '~kui/types/navigation';
import type { MaybeRefOrGetter, Ref } from 'vue';

import type { IaAudiences } from '~/composables/unified/useIaAudience';

import { useCoachAssignedLessons } from '~/composables/coach/useCoachAssignedLessons';
import { IaAudience } from '~/composables/unified/useIaAudience';
import { useRewardsSuppressed } from '~/composables/unified/useRewardsSuppressed';
import { useUserAvatar } from '~/composables/unified/useUserAvatar';
import { useLocaleFormatting } from '~/composables/useLocaleFormatting';
import { INDEX_ILLUSTRATIONS } from '~/constants/indexIllustrations';
import {
  COACH_ROUTE_ACCOUNT,
  COACH_ROUTE_ARTICLES,
  COACH_ROUTE_BADGES,
  COACH_ROUTE_CAREERS,
  COACH_ROUTE_CDP,
  COACH_ROUTE_COURSES,
  COACH_ROUTE_EXPLORE,
  COACH_ROUTE_INDEX,
  COACH_ROUTE_INSTITUTIONS,
  COACH_ROUTE_JOB_CANDIDATES,
  COACH_ROUTE_JOBS,
  COACH_ROUTE_LEARN,
  COACH_ROUTE_LESSONS,
  COACH_ROUTE_PLAN,
  COACH_ROUTE_PORTFOLIO,
  COACH_ROUTE_PROFILE,
  COACH_ROUTE_REWARDS,
  COACH_ROUTE_SAVED,
  COACH_ROUTE_VIDEOS,
} from '~/constants/routes/coach';
import {
  UNIFIED_ROUTE_ABOUT,
  UNIFIED_ROUTE_ARTICLE_ROOT,
  UNIFIED_ROUTE_CAREERS,
  UNIFIED_ROUTE_COURSE,
  UNIFIED_ROUTE_EXPLORE,
  UNIFIED_ROUTE_INSTITUTION,
  UNIFIED_ROUTE_JOB_DETAIL,
  UNIFIED_ROUTE_LEARN,
  UNIFIED_ROUTE_VIDEOS,
} from '~/constants/routes/unified';

/** A place in the IA's bar; a guest's sign-in is marked `isSecondary` so it sits beside the places. */
export type IaNavItem = NavItem & { isSecondary?: boolean };

/**
 * The navigation of the new information architecture, for either audience: the places a student
 * moves between with the two numbers they carry (clarity and coins), or the places a guest can read
 * before signing in. Learn and Explore are the same items for both, so they read the same.
 *
 * `signInItem` is the guest bar's sign-in, from the public layout: its link and entry tracking come
 * from the landing navigation, which knows which lane the page signs a guest up into.
 */
export const useIaNavigation = (
  audience: Ref<IaAudiences>,
  { signInItem }: { signInItem?: MaybeRefOrGetter<IaNavItem | undefined> } = {},
) => {
  const { t } = useI18n();
  const route = useRoute();
  const { user } = useAuth();
  const { formatNumber } = useLocaleFormatting();
  const { avatarIllustration, avatarColor } = useUserAvatar();
  const rewardsSuppressed = useRewardsSuppressed();

  // My Lessons is there for the students a school has set work, as it is in the coach's own menu.
  // The read is a signed-in student's, so a guest's bar never makes it.
  const hasAssignedLessons = audience.value === IaAudience.Student ? useCoachAssignedLessons().hasAny : computed(() => false);

  const signedIn = computed(() => audience.value === IaAudience.Student);

  const isWithin = (path: string) => route.path === path || route.path.startsWith(`${path}/`);

  // Under a careers path, an industry page is the one segment and a career advice article is two:
  // the public one, and inside the coach shell its copy of it.
  const careersPagesAt = (depth: number) => [UNIFIED_ROUTE_CAREERS, ...(signedIn.value ? [COACH_ROUTE_CAREERS] : [])]
    .some(careers => route.path.startsWith(`${careers}/`)
      && route.path.slice(careers.length + 1).split('/').filter(Boolean).length === depth);
  const isIndustryPage = () => careersPagesAt(1);
  const isArticlePage = () => careersPagesAt(2);

  // A place is current on its own page and its sub-pages, and on the pages it opens. A student's
  // places are pages of the coach shell: Learn holds the library's collections, the badges, the
  // articles and the videos, Plan the surfaces its tiles open, and Explore the jobs, institutions,
  // courses and industries its directories open. The public pages a place opens (a course, an
  // industry, a collection, an article, a video) are its own for both audiences.
  const navItem = (to: string, label: string, imagePath: string, isActive = isWithin(to)): NavItem => ({
    to,
    label,
    imagePath,
    isActive,
  });

  const learn = () => navItem(
    signedIn.value ? COACH_ROUTE_LEARN : UNIFIED_ROUTE_LEARN,
    t('unified.nav.learn'),
    `${INDEX_ILLUSTRATIONS}/learn.webp`,
    [
      UNIFIED_ROUTE_LEARN,
      UNIFIED_ROUTE_ARTICLE_ROOT,
      UNIFIED_ROUTE_VIDEOS,
      ...(signedIn.value ? [COACH_ROUTE_LEARN, COACH_ROUTE_BADGES, COACH_ROUTE_ARTICLES, COACH_ROUTE_VIDEOS] : []),
    ].some(isWithin) || isArticlePage(),
  );
  const explore = () => navItem(
    signedIn.value ? COACH_ROUTE_EXPLORE : UNIFIED_ROUTE_EXPLORE,
    t('unified.nav.explore'),
    `${INDEX_ILLUSTRATIONS}/explore.webp`,
    [
      UNIFIED_ROUTE_EXPLORE,
      UNIFIED_ROUTE_JOB_DETAIL,
      UNIFIED_ROUTE_COURSE,
      UNIFIED_ROUTE_INSTITUTION,
      ...(signedIn.value ? [COACH_ROUTE_EXPLORE, COACH_ROUTE_JOBS, COACH_ROUTE_INSTITUTIONS, COACH_ROUTE_COURSES] : []),
    ].some(isWithin) || isIndustryPage(),
  );

  const studentItems = (): NavItem[] => [
    navItem(COACH_ROUTE_INDEX, t('unified.nav.chat'), '/images/illustrations/blinky.webp', route.path === COACH_ROUTE_INDEX),
    ...(hasAssignedLessons.value ? [navItem(COACH_ROUTE_LESSONS, t('unified.nav.myLessons'), '/images/illustrations/notepad.webp')] : []),
    learn(),
    explore(),
    navItem(
      COACH_ROUTE_PLAN,
      t('unified.nav.plan'),
      `${INDEX_ILLUSTRATIONS}/plan.webp`,
      [COACH_ROUTE_PLAN, COACH_ROUTE_CDP, COACH_ROUTE_PORTFOLIO, COACH_ROUTE_SAVED, COACH_ROUTE_JOB_CANDIDATES].some(isWithin),
    ),
    navItem(COACH_ROUTE_ACCOUNT, t('unified.nav.profile'), `${INDEX_ILLUSTRATIONS}/profile.webp`),
  ];

  const guestItems = (): IaNavItem[] => {
    const signIn = toValue(signInItem);

    return [
      learn(),
      explore(),
      navItem(UNIFIED_ROUTE_ABOUT, t('unified.nav.about'), '/images/illustrations/phone-heart.webp'),
      ...(signIn ? [signIn] : []),
    ];
  };

  const navItems = computed<IaNavItem[]>(() => (signedIn.value ? studentItems() : guestItems()));

  // The pills are places too: each is selected on the page it opens.
  const claritySelected = computed(() => isWithin(COACH_ROUTE_PROFILE));
  const coinsSelected = computed(() => isWithin(COACH_ROUTE_REWARDS));

  const clarityScore = computed(() => Math.round(user.value?.considerationScore?.percentage ?? 0));

  const coinBalance = computed(() => (rewardsSuppressed.value ? null : formatNumber(user.value?.latestCoinBalance ?? 0)));

  return {
    signedIn,
    navItems,
    clarityScore,
    coinBalance,
    avatarIllustration,
    avatarColor,
    clarityLink: COACH_ROUTE_PROFILE,
    claritySelected,
    coinsSelected,
    coinsLink: COACH_ROUTE_REWARDS,
  };
};
